import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, MessageCircle, CornerDownRight, Flag } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diffMs = Date.now() - d.getTime();
    const mins = Math.floor(diffMs / 60_000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    if (days < 14) return `${days}d`;
    return d.toLocaleDateString();
  } catch {
    return "";
  }
}

export default function Comments({ conceptId }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [content, setContent] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const load = () => {
    api.get(`/concepts/${conceptId}/comments`)
      .then(({ data }) => setItems(data || []))
      .catch(() => {});
  };

  useEffect(() => { if (conceptId) load(); /* eslint-disable-next-line */ }, [conceptId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Sign in to leave a margin-note."); return; }
    const body = content.trim();
    if (!body) return;
    setSubmitting(true);
    try {
      const payload = { concept_id: conceptId, content: body };
      if (replyTo) payload.parent_id = replyTo.id;
      const { data } = await api.post("/comments", payload);
      setItems((cur) => [...cur, data]);
      setContent("");
      setReplyTo(null);
      toast.success(replyTo ? "Reply posted." : "Margin-note added.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post.");
    } finally {
      setSubmitting(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/comments/${id}`);
      // Hard-delete client-side too: drop the comment and every reply that
      // hangs off it so nothing lingers in the thread.
      setItems((cur) => {
        const doomed = new Set([id]);
        // Walk the parent_id graph until no new descendants are found.
        let grew = true;
        while (grew) {
          grew = false;
          for (const c of cur) {
            if (c.parent_id && doomed.has(c.parent_id) && !doomed.has(c.id)) {
              doomed.add(c.id);
              grew = true;
            }
          }
        }
        return cur.filter((c) => !doomed.has(c.id));
      });
    } catch {
      toast.error("Could not remove.");
    }
  };

  const report = async (id) => {
    const reason = window.prompt("Why are you reporting this? (optional)") ?? "";
    if (reason === null) return;
    try {
      await api.post(`/comments/${id}/report`, { reason });
      toast.success("Reported to the archivists.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not report.");
    }
  };

  // Group: roots + children
  const roots = items.filter((c) => !c.parent_id);
  const childrenOf = (parentId) => items.filter((c) => c.parent_id === parentId);

  return (
    <section data-testid="comments-section" className="mt-24">
      <div className="flex items-center gap-3 mb-3">
        <MessageCircle size={14} className="text-[hsl(var(--accent))]" />
        <div className="label">Margin-notes</div>
        <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {items.length}
        </span>
      </div>
      <p className="serif italic text-muted-foreground mb-6 max-w-xl">
        Leave a thought, a parallel, a memory. Reply to others. Keep it kind.
      </p>

      {/* Composer */}
      <form onSubmit={submit} className="paper p-5 mb-10" data-testid="comments-composer">
        {replyTo && (
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mb-3 flex items-center gap-2">
            <CornerDownRight size={11} />
            Replying to {replyTo.user_name}
            <button
              type="button"
              data-testid="comment-cancel-reply"
              onClick={() => setReplyTo(null)}
              className="ml-2 underline hover:text-foreground"
            >
              cancel
            </button>
          </div>
        )}
        <textarea
          data-testid="comment-input"
          value={content}
          onChange={(e) => setContent(e.target.value)}
          rows={2}
          maxLength={2000}
          placeholder={user ? "Add a margin-note…" : "Sign in to leave a margin-note."}
          disabled={!user}
          className="input-line w-full text-[15px] leading-relaxed resize-none disabled:opacity-60"
        />
        <div className="mt-3 flex items-center justify-between">
          <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
            {content.length}/2000
          </span>
          <button
            type="submit"
            data-testid="comment-submit"
            disabled={submitting || !content.trim() || !user}
            className="btn-gold disabled:opacity-40"
          >
            {submitting ? "Posting…" : (replyTo ? "Reply" : "Post")}
          </button>
        </div>
      </form>

      {roots.length === 0 ? (
        <p className="serif italic text-muted-foreground">No notes yet — be the first.</p>
      ) : (
        <ul className="space-y-8">
          {roots.map((c) => (
            <CommentNode
              key={c.id}
              comment={c}
              replies={childrenOf(c.id)}
              currentUserId={user?.user_id}
              onReply={(target) => { setReplyTo(target); setContent(""); }}
              onRemove={remove}
              onReport={report}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function CommentNode({ comment, replies, currentUserId, onReply, onRemove, onReport }) {
  const isMine = currentUserId && comment.user_id === currentUserId;

  return (
    <li data-testid={`comment-${comment.id}`} className="hairline-b pb-6 last:border-b-0">
      <div className="flex items-start gap-3">
        {comment.user_picture
          ? <img src={comment.user_picture} alt="" className="w-8 h-8 rounded-full" />
          : <div className="w-8 h-8 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-sm">{(comment.user_name || "?")[0]?.toUpperCase()}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 mb-2 flex-wrap">
            <Link
              to={`/u/${comment.user_handle || comment.user_id}`}
              className="serif text-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              {comment.user_name}
            </Link>
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {formatWhen(comment.created_at)}
            </span>
          </div>
          <p className="text-foreground/90 leading-relaxed whitespace-pre-wrap">{comment.content}</p>
          <div className="mt-3 flex items-center gap-4">
            <button
              data-testid={`comment-reply-${comment.id}`}
              onClick={() => onReply(comment)}
              className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              Reply
            </button>
            {isMine && (
              <button
                data-testid={`comment-delete-${comment.id}`}
                onClick={() => onRemove(comment.id)}
                className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--destructive))] transition-colors inline-flex items-center gap-1"
              >
                <Trash2 size={10} />
                Remove
              </button>
            )}
            {!isMine && currentUserId && (
              <button
                data-testid={`comment-report-${comment.id}`}
                onClick={() => onReport(comment.id)}
                className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--destructive))] transition-colors inline-flex items-center gap-1"
              >
                <Flag size={10} />
                Report
              </button>
            )}
          </div>

          {replies.length > 0 && (
            <ul className="mt-6 pl-5 border-l border-border space-y-6">
              {replies.map((r) => (
                <CommentReply
                  key={r.id}
                  comment={r}
                  currentUserId={currentUserId}
                  onReply={onReply}
                  onRemove={onRemove}
                  onReport={onReport}
                />
              ))}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
}

function CommentReply({ comment, currentUserId, onReply, onRemove, onReport }) {
  const isMine = currentUserId && comment.user_id === currentUserId;
  return (
    <li data-testid={`comment-${comment.id}`}>
      <div className="flex items-start gap-3">
        {comment.user_picture
          ? <img src={comment.user_picture} alt="" className="w-6 h-6 rounded-full" />
          : <div className="w-6 h-6 rounded-full bg-[hsl(var(--accent)/0.35)] grid place-items-center serif text-xs">{(comment.user_name || "?")[0]?.toUpperCase()}</div>}
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-3 mb-1 flex-wrap">
            <Link to={`/u/${comment.user_handle || comment.user_id}`} className="serif text-foreground hover:text-[hsl(var(--primary))] transition-colors">
              {comment.user_name}
            </Link>
            <span className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              {formatWhen(comment.created_at)}
            </span>
          </div>
          <p className="text-foreground/90 leading-relaxed text-[15px] whitespace-pre-wrap">{comment.content}</p>
          <div className="mt-2 flex items-center gap-4">
            <button
              data-testid={`comment-reply-${comment.id}`}
              onClick={() => onReply(comment)}
              className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--primary))] transition-colors"
            >
              Reply
            </button>
            {isMine && (
              <button
                data-testid={`comment-delete-${comment.id}`}
                onClick={() => onRemove(comment.id)}
                className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--destructive))] transition-colors inline-flex items-center gap-1"
              >
                <Trash2 size={10} />
                Remove
              </button>
            )}
            {!isMine && currentUserId && (
              <button
                data-testid={`comment-report-${comment.id}`}
                onClick={() => onReport(comment.id)}
                className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground hover:text-[hsl(var(--destructive))] transition-colors inline-flex items-center gap-1"
              >
                <Flag size={10} />
                Report
              </button>
            )}
          </div>
        </div>
      </div>
    </li>
  );
}
